import { MultiSelect, Stack, Text } from "@mantine/core";
import { useMemo } from "react";
import type { IntentQuestion } from "@shared/companyIntent.types";
import {
  collaborationRegionsData,
  deriveCollaborationRegionIdsFromCountries,
  geoCountryOptionId,
  getCollaborationCountriesForRegions,
  resolveCollaborationRegionIds,
  resolveGeoCountryLabel,
} from "@shared/collaborationRegionsCatalog";
import type { QuestionAnswerState } from "./IntentQuestionCard";

/** Modal uses zIndex 1200 — dropdown must render above it. */
const CATALOG_MULTISELECT_COMBOBOX_PROPS = {
  withinPortal: true,
  zIndex: 1400,
} as const;

type IntentAnswerGroupsFieldsProps = {
  question: IntentQuestion;
  answer: QuestionAnswerState;
  onChange: (next: QuestionAnswerState) => void;
  renderOptionGrid: (
    options: IntentQuestion["options"],
    checkedIds: string[],
    onToggle: (id: string) => void,
    idPrefix: string,
    selectionMode: "single" | "multiple",
  ) => React.ReactNode;
};

function deriveRegionIdsFromCountryOptionIds(countryOptionIds: readonly string[]): string[] {
  const labels = countryOptionIds
    .map((id) => resolveGeoCountryLabel(id))
    .filter((label): label is string => Boolean(label));
  return deriveCollaborationRegionIdsFromCountries(labels);
}

function resolveStoredRegionIds(params: {
  storedRegionIds: string[] | undefined;
  suggestedRegionIds?: string[];
  countryIds: string[];
}): string[] {
  if (params.storedRegionIds !== undefined) {
    return resolveCollaborationRegionIds(params.storedRegionIds);
  }
  if (params.suggestedRegionIds?.length) {
    return resolveCollaborationRegionIds(params.suggestedRegionIds);
  }
  return deriveRegionIdsFromCountryOptionIds(params.countryIds);
}

function CatalogGeographyGroup({
  groupPrompt,
  countryIds,
  regionIds,
  onChange,
  suggestedRegionIds,
}: {
  groupPrompt: string;
  countryIds: string[];
  regionIds: string[];
  onChange: (countryIds: string[], regionIds: string[]) => void;
  suggestedRegionIds?: string[];
}) {
  const regionOptions = useMemo(
    () =>
      collaborationRegionsData.regions.map((region) => ({
        value: region.id,
        label: region.name,
      })),
    [],
  );

  const countryOptions = useMemo(() => {
    const countries = getCollaborationCountriesForRegions(regionIds);
    return countries.map((country) => ({
      value: geoCountryOptionId(country),
      label: country,
    }));
  }, [regionIds]);

  const allowedCountryIdSet = useMemo(
    () => new Set(countryOptions.map((option) => option.value)),
    [countryOptions],
  );

  const visibleCountryIds = useMemo(
    () => countryIds.filter((id) => allowedCountryIdSet.has(id)),
    [allowedCountryIdSet, countryIds],
  );

  const handleRegionsChange = (nextRegionIds: string[]) => {
    const normalizedRegionIds = resolveCollaborationRegionIds(nextRegionIds);
    const allowedCountryIds = new Set(
      getCollaborationCountriesForRegions(normalizedRegionIds).map((c) => geoCountryOptionId(c)),
    );
    const nextCountryIds = countryIds.filter((id) => allowedCountryIds.has(id));
    onChange(nextCountryIds, normalizedRegionIds);
  };

  return (
    <Stack gap={6}>
      <Text fw={600} size="sm" c="slate.8">
        {groupPrompt}
      </Text>
      <Text size="xs" c="slate.5">
        {suggestedRegionIds?.length
          ? "Suggested regions from your profile — adjust as needed. Countries are optional."
          : "Choose region(s). Countries are optional."}
      </Text>
      <MultiSelect
        label="Regions"
        withAsterisk
        placeholder="Select one or more regions"
        data={regionOptions}
        value={regionIds}
        onChange={handleRegionsChange}
        searchable
        clearable
        maxDropdownHeight={280}
        comboboxProps={CATALOG_MULTISELECT_COMBOBOX_PROPS}
      />
      <MultiSelect
        label="Countries (optional)"
        placeholder={regionIds.length > 0 ? "Select countries" : "Select regions first"}
        data={countryOptions}
        value={visibleCountryIds}
        onChange={(next) => onChange(next, regionIds)}
        searchable
        clearable
        disabled={regionIds.length === 0}
        maxDropdownHeight={280}
        nothingFoundMessage={
          regionIds.length > 0 ? "No countries for selected regions" : "Select regions first"
        }
        comboboxProps={CATALOG_MULTISELECT_COMBOBOX_PROPS}
      />
    </Stack>
  );
}

export default function IntentAnswerGroupsFields({
  question,
  answer,
  onChange,
  renderOptionGrid,
}: IntentAnswerGroupsFieldsProps) {
  const groups = question.answerGroups ?? [];

  const updateGroup = (groupId: string, selectedIds: string[], regionIds?: string[]) => {
    onChange({
      ...answer,
      groupSelections: {
        ...(answer.groupSelections ?? {}),
        [groupId]: selectedIds,
      },
      groupRegionIds:
        regionIds !== undefined
          ? {
              ...(answer.groupRegionIds ?? {}),
              [groupId]: regionIds,
            }
          : answer.groupRegionIds,
    });
  };

  const toggleGroupOption = (
    groupId: string,
    optionId: string,
    selectionMode: "single" | "multiple",
  ) => {
    const current = answer.groupSelections?.[groupId] ?? [];
    if (selectionMode === "single") {
      updateGroup(groupId, [optionId]);
      return;
    }
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    updateGroup(groupId, next);
  };

  return (
    <Stack gap="lg" mt="md">
      {groups.map((group) => {
        if (group.inputType === "catalog_geography") {
          const countryIds = answer.groupSelections?.[group.id] ?? [];
          const regionIds = resolveStoredRegionIds({
            storedRegionIds: answer.groupRegionIds?.[group.id],
            suggestedRegionIds: group.suggestedRegionIds,
            countryIds,
          });

          return (
            <CatalogGeographyGroup
              key={group.id}
              groupPrompt={group.prompt}
              countryIds={countryIds}
              regionIds={regionIds}
              suggestedRegionIds={group.suggestedRegionIds}
              onChange={(nextCountryIds, nextRegionIds) =>
                updateGroup(group.id, nextCountryIds, resolveCollaborationRegionIds(nextRegionIds))
              }
            />
          );
        }

        return (
          <Stack key={group.id} gap={4}>
            <Text fw={600} size="sm" c="slate.8">
              {group.prompt}
            </Text>
            <Text size="xs" c="slate.5">
              {group.selectionMode === "single" ? "Choose one" : "Select all that apply"}
            </Text>
            {renderOptionGrid(
              group.options,
              answer.groupSelections?.[group.id] ?? [],
              (optionId) => toggleGroupOption(group.id, optionId, group.selectionMode),
              `${question.id}-${group.id}`,
              group.selectionMode,
            )}
          </Stack>
        );
      })}

    </Stack>
  );
}
